/* Product voice.
 *
 * The strings marked VERBATIM are lifted from kilroy-app-ui.html and are part
 * of the approved contract. Do not rewrite them, do not "improve" them, do not
 * let them drift between the phone and the desk. They live here so that can't
 * happen.
 *
 * The rest follow the same register: plain verbs, sentence case, active voice,
 * no apology, no filler. An action keeps its name through the whole flow — the
 * button that says "Save" produces a toast that says "Saved".
 */

/** VERBATIM — the sync narration. This animation is the product demo. */
export const SYNC_NARRATION = {
  draining: 'Signal found. Draining the queue — records first, photos and audio behind.',
  synced:
    'All synced. The whole team can see today’s ground — same evening, not season’s end.',
} as const;

/** VERBATIM — the compass warning on the structural reading-source toggle. */
export const COMPASS_WARNING =
  'Mineralized ground lies to compasses — your instrument is truth. Each reading records its source.';

/** VERBATIM — the sub-caption under the Save button. */
export const SAVE_CAPTION =
  'saves on-device instantly · original is immutable · corrections append';

/** VERBATIM — the priorities footer. */
export const RECTIFIED_FOOTER = 'Rectified by Patrick · every change keeps its why';

/* ── Status pill. Never an error, always a state. There is no "you're offline"
      message anywhere in this app. ─────────────────────────────────────────── */
export const PILL = {
  offline: (n: number) => (n === 0 ? 'Offline' : `Offline · ${n} queued`),
  syncing: (n: number) => `Syncing ${n}…`,
  synced: 'Synced',
  /** Records are through; media is still climbing. */
  media: (n: number) => `Records synced · ${n} file${n === 1 ? '' : 's'} behind`,
} as const;

/* ── Capture ────────────────────────────────────────────────────────────── */
export const CAPTURE = {
  gpsSettling: 'Settling…',
  gpsLocked: 'Locked',
  /* Two failure states, and they are not the same problem. "Off" is something
     the person can fix in Settings; "no signal" is something they can only walk
     out from under. Neither may render as "settling" — a failure that looks
     like progress is the reason someone stands still waiting for a lock that
     is never coming. */
  gpsOff: 'Off',
  gpsNoSignal: 'No signal',
  gpsDenied: 'Location off — the record still saves, without coordinates.',
  gpsUnavailable: 'No position available — the record still saves without one.',
  gpsCaption: 'Accuracy is recorded either way. A rough fix beats a missed observation.',
  voiceHint: 'Hold nothing back. Transcribes on sync.',
  voiceRecording: 'Recording',
  photoEmpty: 'Add a photo',
  photoCover: 'Cover',
  retakeNote: 'Retaking supersedes the original. Nothing is deleted.',
  savedToast: (n: number) => `Saved on-device · ${n} queued`,
  draftRestored: 'Draft restored.',
  requiredSource: 'Pick where this reading came from before saving.',
} as const;

/* ── Assignment ─────────────────────────────────────────────────────────── */
export const ASSIGNMENT = {
  captureAgainst: 'Capture against this',
  markWalked: 'Mark walked — nothing further',
  walkedPrompt: 'Add a voice note on why nothing, if it helps the next person.',
  progress: (found: number, expected?: number) =>
    expected ? `${found} of ~${expected} captured` : `${found} captured`,
  forWhom: (author: string, title: string) => `for ${author}’s ${title}`,
} as const;

/* ── Install / onboarding. The reason, not the ritual. ──────────────────── */
export const INSTALL = {
  why: 'Installing protects your field records from browser cleanup and unlocks offline storage.',
  notInstalled: 'Not installed — records at risk',
  banner:
    'Add Kilroy to your home screen. Until you do, the browser can clear a day’s work.',
  done: 'Installed. Storage is yours now.',
} as const;

/* ── Provenance badges. These follow a record everywhere, forever. ──────── */
export const BADGE = {
  backfilled: 'backfilled from notebook',
  machineTranscript: 'machine transcript · system:transcriber',
  superseded: 'superseded',
  placeholderBounds: 'placeholder boundary',
} as const;

/* ── Empty states are an invitation to act, never a mood. ──────────────── */
export const EMPTY = {
  today: 'Nothing captured yet today.',
  todayAction: 'The first observation takes about twenty seconds.',
  assignments: 'No open assignments.',
  assignmentsDesk: 'Draw a polygon on the map to route ground to someone.',
  priorities: 'No priority items yet.',
  prioritiesDesk: 'Create one from a map selection, or start from a blank item.',
  queue: 'Queue is empty.',
} as const;

/* ── Compliance boundary. Stuart Hutchin is the QP; he is not a user.
      From Brandon's q.03. ───────────────────────────────────────────────── */
export const COMPLIANCE = {
  workingRecord: 'working record — not a disclosure document',
  qpNote:
    'Evidence reaches a 43-101 technical report only through provenance, discussion, and QP approval.',
} as const;
